/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"

import React, { useEffect } from "react"

import { useState, useMemo } from "react"
import {
  Search,
  Bell,
  AlertTriangle,
  FileText,
  Gavel,
  Clock,
  Settings,
  Eye,
  UserCheck,
  Archive,
  ChevronRight,
  User,
  MapPin,
  RefreshCw,
  ChevronLeft,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { cn } from "@/lib/utils"
import apiService from "@/services/apiService"
import { useAppStore } from "@/store/appStore"
import { toast } from "sonner"

const getNotificationIcon = (type: string) => {
  switch (type) {
    case "highRisk":
      return AlertTriangle
    case "lowRisk":
      return FileText
    case "mediumRisk":
      return Gavel
    case "reminder":
      return Clock
    case "system":
      return Settings
    default:
      return Bell
  }
}

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "critical":
      return "border-l-red-500 bg-red-50 dark:bg-red-950/20"
    case "high":
      return "border-l-orange-500 bg-orange-50 dark:bg-orange-950/20"
    case "medium":
      return "border-l-blue-500 bg-blue-50 dark:bg-blue-950/20"
    case "low":
      return "border-l-gray-500 bg-gray-50 dark:bg-gray-950/20"
    default:
      return "border-l-gray-300"
  }
}

const formatTimeAgo = (inputDate: any) => {
  const date = new Date(inputDate)
  const now = new Date()
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

  if (diffInMinutes < 1) return "Just now"
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
  return `${Math.floor(diffInMinutes / 1440)}d ago`
}

export default function Notifications() {
  const { _notifications } = useAppStore()
  const [categories, setCategories] = useState<any>([])
  const [selectedCategory, setSelectedCategory] = useState("all")
  const [selectedNotification, setSelectedNotification] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [priorityFilter, setPriorityFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all")
  const [notifications, setNotifications] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const PAGE_SIZE = 50

  useEffect(() => {
    fetchTodaysNotifications()
  }, [])

  const filteredNotifications = useMemo(() => {
    setCurrentPage(1)
    return notifications.filter((notification:any) => {
      const matchesCategory =
        selectedCategory === "all" ||
        notification.type === selectedCategory

      const matchesSearch =
        searchQuery === "" ||
        notification.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        notification.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        notification.caseNumber?.toLowerCase().includes(searchQuery.toLowerCase())

      const matchesPriority = priorityFilter === "all" || notification.priority === priorityFilter
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "unread" && !notification.isRead) ||
        (statusFilter === "read" && notification.isRead) ||
        (statusFilter === "action-required" && notification.actionRequired)

      return matchesCategory && matchesSearch && matchesPriority && matchesStatus
    })
  }, [notifications, selectedCategory, searchQuery, priorityFilter, statusFilter])

  const totalPages = Math.max(1, Math.ceil(filteredNotifications.length / PAGE_SIZE))
  const paginatedNotifications = filteredNotifications.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const markAsRead = (id: string) => {
    setNotifications((prev:any) => prev.map((n:any) => (n.id === id ? { ...n, isRead: true } : n)))
  }

  const markAllAsRead = () => {
    setNotifications((prev:any) => prev.map((n:any) => ({ ...n, isRead: true })))
  }

  const refreshNotification = async () => {
    await apiService.get("/notifications/manualNotificationCheck");
    fetchTodaysNotifications()
    toast.success("Notifications refreshed successfully")
  }

  const selectedNotificationData:any = notifications.find((n:any) => n.id === selectedNotification)

  const fetchTodaysNotifications = async () => {
    const processedNotifications:any = _notifications.data.map((notification: any) => ({
      ...notification,
      category: notification.type || "General",
      description: notification.message || "No description provided",
      isRead: notification.isRead || false,
      officer: "Unknown Officer",
      caseNumber: notification.caseNumber || null,
      location: notification.location || "Unknown Location",
      actionRequired: notification.actionRequired || false,
      priority: notification.title.includes("high risk") || notification.type === "REPEAT_OFFENDER_SUSPECT" || notification.type === "REPEAT_OFFENDER_VICTIM" ? "high" : notification.title.includes("low risk") ? "low" : "medium",
      timestamp: new Date(notification.timestamp),
    }))
    setNotifications(processedNotifications);
    setCategories([
      { id: "all", label: "All Notifications", count: processedNotifications.length },
      { id: "GENERAL_DIARY_NO_CRPC129", label: "Missing 111/130", count: processedNotifications.filter((n:any) => n.type === "GENERAL_DIARY_NO_CRPC129").length },
      { id: "CRPC129_NO_BNSL129", label: "Missing 116_3/135_3", count: processedNotifications.filter((n:any) => n.type === "CRPC129_NO_BNSL129").length },
      { id: "CRPC129_NO_CRPC_BOND_ORDER", label: "Missing 117/136", count: processedNotifications.filter((n:any) => n.type === "CRPC129_NO_CRPC_BOND_ORDER").length },
      { id: "REPEAT_OFFENDER_SUSPECT", label: "Repeat Suspects", count: processedNotifications.filter((n:any) => n.type === "REPEAT_OFFENDER_SUSPECT").length },
      { id: "REPEAT_OFFENDER_VICTIM", label: "Repeat Victims", count: processedNotifications.filter((n:any) => n.type === "REPEAT_OFFENDER_VICTIM").length },
      { id: "system", label: "System Notices", count: processedNotifications.filter((n:any) => n.type === "system").length },
    ])
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Command Bar */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-black text-gray-900 dark:text-white uppercase tracking-wide">
              NOTIFICATION CENTER
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {new Date().toLocaleDateString("en-IN", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search notifications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-80"
              />
            </div>
            <Button onClick={markAllAsRead} variant="outline" size="sm" className="cursor-pointer">
              Mark All Read
            </Button>
            <Button onClick={refreshNotification} variant="outline" size="sm" className="cursor-pointer" title="Refresh Notifications">
              <RefreshCw className="cursor-pointer" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex h-[calc(100vh-120px)]">
        {/* Filter Sidebar */}
        <div className="w-80 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 p-6">
          <div className="space-y-6">
            {/* Categories */}
            <div>
              <h3 className="font-heading text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide mb-3">
                Categories
              </h3>
              <div className="space-y-1">
                {categories.map((category:any) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                      selectedCategory === category.id
                        ? "bg-primary text-white"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700",
                    )}
                  >
                    <span>{category.label}</span>
                    <Badge variant="secondary" className="ml-2">
                      {category.count}
                    </Badge>
                  </button>
                ))}
              </div>
            </div>

            <Separator />

            {/* Advanced Filters */}
            <div>
              <h3 className="font-heading text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wide mb-3">
                Filters
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                    Priority Level
                  </label>
                  <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                    <SelectTrigger className="mt-1 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priorities</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                    Status
                  </label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="mt-1 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="unread">Unread</SelectItem>
                      <SelectItem value="read">Read</SelectItem>
                      <SelectItem value="action-required">Action Required</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex">
          {/* Notification Feed */}
          <div className="flex-1 p-6">
            <ScrollArea className="h-full">
              <div className="space-y-4">
                {filteredNotifications.length === 0 ? (
                  <div className="text-center py-12">
                    <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">No notifications found</h3>
                    <p className="text-gray-600 dark:text-gray-400">Try adjusting your filters or search query.</p>
                  </div>
                ) : (
                  paginatedNotifications.map((notification:any) => {
                    const Icon = getNotificationIcon(notification.type)
                    return (
                      <Card
                        key={notification.id}
                        className={cn(
                          "cursor-pointer transition-all hover:shadow-md border-l-4",
                          getPriorityColor(notification.priority),
                          !notification.isRead && "ring-2 ring-blue-200 dark:ring-blue-800",
                          selectedNotification === notification.id && "ring-2 ring-police-primary",
                        )}
                        onClick={() => {
                          setSelectedNotification(notification.id)
                          if (!notification.isRead) markAsRead(notification.id)
                        }}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div
                              className={cn(
                                "p-2 rounded-lg",
                                notification.priority === "critical"
                                  ? "bg-red-100 text-red-600 dark:bg-red-900/20"
                                  : notification.priority === "high"
                                    ? "bg-orange-100 text-orange-600 dark:bg-orange-900/20"
                                    : "bg-blue-100 text-blue-600 dark:bg-blue-900/20",
                              )}
                            >
                              <Icon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-body font-semibold text-gray-900 dark:text-white text-sm">
                                  {notification.title}
                                </h4>
                                {notification.actionRequired && (
                                  <Badge variant="destructive" className="text-xs">
                                    Action Required
                                  </Badge>
                                )}
                                {!notification.isRead && <div className="w-2 h-2 bg-blue-500 rounded-full" />}
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                {notification.description}
                              </p>
                              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {formatTimeAgo(notification.createdAt)}
                                </span>
                                {notification.caseNumber && (
                                  <span className="flex items-center gap-1">
                                    <Gavel className="h-3 w-3" />
                                    {notification.caseNumber}
                                  </span>
                                )}
                                <span className="flex items-center gap-1 hidden">
                                  <User className="h-3 w-3" />
                                  {notification.officer}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })
                )}
              </div>
              {filteredNotifications.length > 0 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, filteredNotifications.length)} of {filteredNotifications.length}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium">{currentPage} / {totalPages}</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Action Drawer */}
          {selectedNotificationData && (
            <div className="w-96 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 p-6">
              <ScrollArea className="h-full">
                <div className="space-y-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {React.createElement(getNotificationIcon(selectedNotificationData.type), {
                        className: "h-5 w-5 text-police-primary",
                      })}
                      <Badge variant="outline">{selectedNotificationData.category}</Badge>
                    </div>
                    <h2 className="font-heading text-lg font-bold text-gray-900 dark:text-white">
                      {selectedNotificationData.title}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                      {selectedNotificationData.description}
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <label className="font-medium text-gray-900 dark:text-white">Priority</label>
                        <Badge
                          variant={selectedNotificationData.priority === "critical" ? "destructive" : "secondary"}
                          className="mt-1 capitalize"
                        >
                          {selectedNotificationData.priority}
                        </Badge>
                      </div>
                      <div>
                        <label className="font-medium text-gray-900 dark:text-white">Status</label>
                        <Badge variant={selectedNotificationData.isRead ? "secondary" : "default"} className="mt-1">
                          {selectedNotificationData.isRead ? "Read" : "Unread"}
                        </Badge>
                      </div>
                    </div>

                    {selectedNotificationData.caseNumber && (
                      <div>
                        <label className="font-medium text-gray-900 dark:text-white">Case Number</label>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {selectedNotificationData.caseNumber}
                        </p>
                      </div>
                    )}

                    <div className="hidden">
                      <label className="font-medium text-gray-900 dark:text-white">Assigned Officer</label>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {selectedNotificationData.officer}
                      </p>
                    </div>

                    <div className="hidden">
                      <label className="font-medium text-gray-900 dark:text-white">Location</label>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {selectedNotificationData.location}
                      </p>
                    </div>

                    <div>
                      <label className="font-medium text-gray-900 dark:text-white">Date</label>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {selectedNotificationData.createdAt ? new Date(selectedNotificationData.createdAt).toLocaleDateString("en-IN") : "-"}
                      </p>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-3 hidden">
                    <h3 className="font-heading text-sm font-bold text-gray-900 dark:text-white uppercase">Actions</h3>
                    <div className="space-y-2">
                      <Button className="w-full justify-start" variant="default">
                        <Eye className="h-4 w-4 mr-2" />
                        View Details
                      </Button>
                      <Button className="w-full justify-start bg-transparent" variant="outline">
                        <UserCheck className="h-4 w-4 mr-2" />
                        Acknowledge
                      </Button>
                      <Button className="w-full justify-start bg-transparent" variant="outline">
                        <Archive className="h-4 w-4 mr-2" />
                        Archive
                      </Button>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
